import { Component, Inject, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import {
  ActionFailed,
  ChannelNameTokens,
  ChannelService,
  ConnectedChannelAccountResponse,
  Destroyable,
  Toaster,
} from '@tokeet-frontend/tv3-platform'
import { selectAllRentals } from '@tokeet-frontend/tv3-platform'
import { filter, finalize, map, observeOn, startWith, switchMap, tap } from 'rxjs/operators'
import { asyncScheduler, combineLatest, Observable, of } from 'rxjs'
import { SaveForm } from '@tokeet-frontend/tv3-platform'
import { ConnectionAirbnbService } from '@tv3/store/connection/connection-airbnb.service'
import {
  CreateConnectionsForChannel,
  CreateConnectionsForChannelComplete,
} from '@tv3/store/connection/connection.actions'
import { Actions, ofType } from '@ngrx/effects'
import { AirbnbChannelService } from '@tokeet-frontend/channels'

@Component({
  selector: 'app-create-airbnb-listing-dialog',
  host: { class: 'modal-content' },
  templateUrl: './create-airbnb-listing-dialog.component.html',
  styleUrls: ['./create-airbnb-listing-dialog.component.scss'],
})
export class CreateAirbnbListingDialogComponent extends Destroyable implements OnInit {
  form = this.fb.group({
    account: [undefined, [Validators.required]],
    rentalId: [undefined, [Validators.required]],
    promotion: [false],
  })

  accounts$ = this.channelService.getConnectedAccounts(this.data.channelId)
  rentals$ = this.store.pipe(observeOn(asyncScheduler), select(selectAllRentals))
  connectionChannelId: string

  isActiveAccount: boolean

  isLoading = false

  // Airbnb rejects listing creation when the name sent (rental display name, falling back to name) exceeds 50 chars
  readonly listingNameMaxLength = 50
  listingName = ''
  listingNameTooLong = false

  constructor(
    public dialogRef: MatDialogRef<CreateAirbnbListingDialogComponent>,
    private fb: FormBuilder,
    private store: Store<fromRoot.State>,
    private toaster: Toaster,
    private actions$: Actions,
    private channelService: ChannelService,
    private airbnbChannelService: AirbnbChannelService,
    private airbnbService: ConnectionAirbnbService,
    @Inject(MAT_DIALOG_DATA) public data: { channelId: string }
  ) {
    super()
  }

  ngOnInit() {
    combineLatest([
      this.form.get('rentalId').valueChanges.pipe(startWith(this.form.get('rentalId').value)),
      this.rentals$,
    ]).subscribe(([rentalId, rentals]) => {
      const rental = rentals?.find((r) => r.id === rentalId)
      this.listingName = rental ? rental.displayName || rental.name || '' : ''
      this.listingNameTooLong = this.listingName.length > this.listingNameMaxLength
    })

    this.form
      .get('account')
      .valueChanges.pipe(
        startWith(this.form.get('account').value),
        filter((account) => !!account),
        switchMap((account) => this.airbnbService.getPropertyStatus(account.propertyId).pipe(map((status) => !!status)))
      )
      .subscribe((isActive) => {
        this.isActiveAccount = isActive
      })

    this.actions$
      .pipe(
        ofType(CreateConnectionsForChannelComplete),
        switchMap((res) => {
          const { account, promotion }: { account: ConnectedChannelAccountResponse; promotion: boolean } =
            this.form.getRawValue()
          if (!promotion) {
            return of(null)
          }
          return this.airbnbChannelService.createListingPromotion(account.propertyId, account.channelId, [res.id]).pipe(
            tap(() => {
              this.toaster.success('Listing promotion created successfully')
            })
          )
        }),
        finalize(() => {
          this.isLoading = false
        })
      )
      .subscribe({
        next: () => {
          this.close()
        },
        error: (error) => {
          this.store.dispatch(ActionFailed({ error }))
        },
      })
  }

  close() {
    this.dialogRef.close()
  }

  @SaveForm()
  onSave(form: FormGroup) {
    this.isLoading = true
    const {
      rentalId,
      account,
      promotion,
    }: { rentalId: string; account: ConnectedChannelAccountResponse; promotion: boolean } = form.getRawValue()
    this.store.dispatch(
      CreateConnectionsForChannel({
        channelName: ChannelNameTokens.AirBnBV2API,
        data: {
          // the selected account's connection-level channelId, not the generic channel id —
          // the backend links the created listing within this connection
          channelId: account.channelId || this.data.channelId,
          propertyId: account.propertyId,
          rental_id: rentalId,
          api: ChannelNameTokens.AirBnBV2API,
          promotion,
        },
      })
    )
  }
}
